import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Field from '../../../../../../../../../../components/Form/Field'
import FieldLabelWrapper from '../../../../../../../../../../features/components/Form/FieldLabelWrapper'
import ToggleField from '../../../../../../../../../../components/Form/ToggleField'
import SelectedAdPlacementsTable from './SubAllocationTables/SelectedAdPlacementsTable'
import SelectedAdUnitsTable from './SubAllocationTables/SelectedAdUnitsTable'
import AdvancedSettings from './AdvancedSettings'
import Tabs from '../../../../../../../../../../features/components/Tabs'
import SingleAllocationFields from './SubAllocationTables/SingleAllocationFields'

import {
  calculateTotalRightValue,
  calculateTotalAllocation,
  getShowTotalRightValue,
  getRateFieldLabel,
  getBillingEventType
} from '../helpers'
import { formatNumber } from '../../../../../../../../../../helpers/numbers'

import {
  getGoogleAdManagerAdUnitsSelector,
  getGoogleAdManagerAdUnitsWasLoadedSelector,
  getGoogleAdManagerAdUnitWasLoadedSelector
} from '../../../../../../../../../../modules/selectors/googleAdManager'
import {
  getGoogleAdManagerAdUnit,
  getGoogleAdManagerAdUnits
} from '../../../../../../../../../../modules/actions/googleAdManager'

import {
  ALLOCATION_TYPE,
  allocationTypeLabels,
  allocationTypeOptions,
  allocationTypes,
  BILLING_EVENT,
  billingEventOptions,
  GOOGLE_AD_MANAGER_AD_ACCOUNT,
  INVENTORY_TYPE,
  INVENTORY_TYPE_AD_PLACEMENTS,
  INVENTORY_TYPE_AD_UNITS,
  inventoryTypeOptions,
  IS_SUB_ALLOCATION
} from '../../fields'
import { selectedPartnershipSelector } from '../../../../../../../../../../modules/selectors/partnerships'
import { selfAccountSelector } from '../../../../../../../../../../modules/selectors/selfAccounts'

import { GAM_NETWORK_CODE } from '../fields'

import useDrawerFormStyles from '../../../../../../../../../../styles/common/drawerForms'
import useStyles from './styles'

const InventoryAccessAndAllocationSection = ({ formik }) => {
  const { t } = useTranslation()
  const drawerFormClasses = useDrawerFormStyles()
  const classes = useStyles()
  const dispatch = useDispatch()

  const getGoogleAdManagerAdUnitsWasLoaded = useSelector(getGoogleAdManagerAdUnitsWasLoadedSelector)
  const googleAdManagerAdUnits = useSelector(getGoogleAdManagerAdUnitsSelector)
  const getGoogleAdManagerAdUnitWasLoaded = useSelector(getGoogleAdManagerAdUnitWasLoadedSelector)
  const { [GOOGLE_AD_MANAGER_AD_ACCOUNT]: gamAccount } = useSelector(selectedPartnershipSelector)
  const selfAccount = useSelector(selfAccountSelector)
  const gamNetworkCode = selfAccount?.[GAM_NETWORK_CODE]

  const { values, setFieldValue } = formik

  const isInventoryTypeAdUnits = values[INVENTORY_TYPE] === INVENTORY_TYPE_AD_UNITS

  const topParentAdUnitId = useMemo(() => {
    return googleAdManagerAdUnits?.find(unit => unit.parent_id === null)?.id
  }, [googleAdManagerAdUnits])

  useEffect(() => {
    if (isInventoryTypeAdUnits && !getGoogleAdManagerAdUnitsWasLoaded) {
      dispatch(
        getGoogleAdManagerAdUnits({
          network_code: gamNetworkCode,
          fields: ['parent_id']
        })
      )
    }
  }, [dispatch, isInventoryTypeAdUnits, getGoogleAdManagerAdUnitsWasLoaded, gamNetworkCode])

  useEffect(() => {
    if (isInventoryTypeAdUnits && !getGoogleAdManagerAdUnitWasLoaded && topParentAdUnitId) {
      dispatch(
        getGoogleAdManagerAdUnit({
          id: topParentAdUnitId,
          network_code: gamNetworkCode,
          include_tree: true
        })
      )
    }
  }, [dispatch, isInventoryTypeAdUnits, getGoogleAdManagerAdUnitWasLoaded, topParentAdUnitId, gamNetworkCode])

  const allocationTypeLabel = useMemo(() => {
    return allocationTypeLabels[values[ALLOCATION_TYPE]]
  }, [values])

  const allocationPriceLabel = useMemo(() => {
    const billingEventType = getBillingEventType(values[ALLOCATION_TYPE], values[BILLING_EVENT])
    return getRateFieldLabel(billingEventType)
  }, [values])

  const showTotalValueOfRight = useMemo(() => {
    return getShowTotalRightValue(values[ALLOCATION_TYPE])
  }, [values])

  const totalAllocation = useMemo(() => {
    return calculateTotalAllocation(values)
  }, [values])

  const totalRightValue = useMemo(() => {
    return calculateTotalRightValue(values)
  }, [values])

  const selectInventoryTypeHandler = useCallback(
    value => {
      setFieldValue(INVENTORY_TYPE, value)
    },
    [setFieldValue]
  )

  return (
    <>
      <section className={drawerFormClasses.section}>
        <h4 className={drawerFormClasses.sectionTitle}>{t('Inventory access & allocation')}</h4>
        <FieldLabelWrapper label={t('Allocation type')} labelFor={ALLOCATION_TYPE}>
          <Field formik={formik} name={ALLOCATION_TYPE} options={allocationTypeOptions} />
        </FieldLabelWrapper>
        {values[ALLOCATION_TYPE] === allocationTypes.budget && (
          <FieldLabelWrapper label={t('Billing event')} labelFor={BILLING_EVENT}>
            <Field formik={formik} name={BILLING_EVENT} options={billingEventOptions} />
          </FieldLabelWrapper>
        )}
        <br />
        <ToggleField
          name={IS_SUB_ALLOCATION}
          setFieldValue={setFieldValue}
          isEnabled={values[IS_SUB_ALLOCATION]}
          label="Allocate separate budgets to individual pieces of ad inventory"
        />

        {!values[IS_SUB_ALLOCATION] && <SingleAllocationFields formik={formik} />}

        <FieldLabelWrapper
          label={t('Inventory type')}
          labelFor={INVENTORY_TYPE}
          className={classes.inventoryTypeTabsWrapper}
        >
          <Tabs
            onSelectValue={selectInventoryTypeHandler}
            selectedValue={values[INVENTORY_TYPE]}
            options={inventoryTypeOptions}
          />
        </FieldLabelWrapper>
        {values[INVENTORY_TYPE] === INVENTORY_TYPE_AD_PLACEMENTS ? (
          <SelectedAdPlacementsTable formik={formik} allocationPriceLabel={allocationPriceLabel} />
        ) : (
          <SelectedAdUnitsTable
            formik={formik}
            topParentAdUnitId={topParentAdUnitId}
            allocationPriceLabel={allocationPriceLabel}
          />
        )}

        {showTotalValueOfRight && (
          <div>
            {values[IS_SUB_ALLOCATION] && values[ALLOCATION_TYPE] !== allocationTypes.budget && (
              <p className={classes.totalValue}>
                {t(`Total ${allocationTypeLabel} allocation = ${formatNumber(totalAllocation)}`)}
              </p>
            )}
            <p className={classes.totalValue}>
              {values[ALLOCATION_TYPE] === allocationTypes.budget
                ? t(
                    `Value of total allocation = ${gamAccount.currency_symbol}${formatNumber(totalRightValue, { max: 2, min: 2 })}`
                  )
                : t(
                    `Value of total ${allocationTypeLabel} allocation = ${gamAccount.currency_symbol}${formatNumber(
                      totalRightValue
                    )}`
                  )}
            </p>
          </div>
        )}
      </section>
      <AdvancedSettings formik={formik} />
    </>
  )
}

export default InventoryAccessAndAllocationSection
